import { reverse } from 'named-urls';
import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useDashboardPermissions } from '@components';
import { ButtonClean, useModalContext } from '@fourthwall/components';
import { pluralize } from '@fourthwall/utils/dist/string';
import { routing } from '@utils/routing';
import { GiveawayTwitchRedo } from '../modules/GiveawayTwitchRedo';
import { useGiveawayContributionQuery } from '../queries/useGiveawayContributionQuery';
import { isAvailableGift } from '../utils/isAvailableGift';
import { isCancelledGiftWithoutWinner } from '../utils/isCancelledGiftWithoutWinner';
import { isGiftCancelled } from '../utils/isGiftCancelled';
import { isGiftWithoutWinner } from '../utils/isGiftWithoutWinner';
export const useGiveawayBanners = () => {
    const { giveawayId = '' } = useParams();
    const { giveawayContributionQuery } = useGiveawayContributionQuery([{ giveawayId }]);
    const giveaway = giveawayContributionQuery.data;
    const location = useLocation();
    const { hasPermission } = useDashboardPermissions();
    const { open } = useModalContext();
    if (!giveaway)
        return [];
    const giftsWithAvailableWinners = giveaway.gifts.filter((gift) => ['GIFT_REDEEMED', 'GIFT_AVAILABLE', 'GIFT_CHANGED_TO_PROMOTION'].includes(gift.type));
    const giftsWithCancelledWinners = giveaway.gifts.filter(isGiftCancelled);
    const giftsWithWinners = [...giftsWithAvailableWinners, ...giftsWithCancelledWinners];
    const giftsWithoutWinners = giveaway.gifts.filter((gift) => isGiftWithoutWinner(gift) || isCancelledGiftWithoutWinner(gift));
    const giftsAvailable = giveaway.gifts.filter(isAvailableGift);
    const giftsAvailableWithoutWinners = giveaway.gifts.filter(isGiftWithoutWinner);
    const showRedoButton = (hasPermission('contributions.orders.redoGiveaway') && !!giftsAvailable.length) ||
        !!giftsAvailableWithoutWinners.length;
    const handleRedoGiveawayClick = () => {
        open(GiveawayTwitchRedo, { giveawayId });
    };
    const redoButton = showRedoButton ? (React.createElement(React.Fragment, null,
        "You may",
        ' ',
        React.createElement(ButtonClean, { appearance: "warning", label: "redo the giveaway", onClick: handleRedoGiveawayClick }))) : null;
    const isMembershipsGift = (orderFriendlyId) => orderFriendlyId.match(/^0+$/);
    const getOrderLinkTo = (orderId) => {
        const locationState = {
            from: { label: `Giveaway ${giveaway.friendlyId}`, location },
        };
        return {
            pathname: reverse(routing.contributions.orders.order.self, { orderId }),
            state: locationState,
        };
    };
    const getPromotionLinkTo = (promotionId) => {
        return {
            pathname: reverse(routing.promotions.promotionCodes.promotionCode.self, { promotionId }),
        };
    };
    const getChildren = () => {
        if (!giftsWithWinners.length) {
            return React.createElement(React.Fragment, null,
                "This gift currently has no winners. ",
                redoButton);
        }
        if (giveaway.gifts.length === 1) {
            const gift = giveaway.gifts[0];
            if (gift.type === 'GIFT_REDEEMED') {
                const { supporter, orderId, orderFriendlyId } = gift;
                return (React.createElement(React.Fragment, null,
                    "This gift has a selected winner:",
                    React.createElement("br", null),
                    supporter.username || supporter.email,
                    !isMembershipsGift(orderFriendlyId) && (React.createElement(React.Fragment, null,
                        ' ',
                        "(",
                        React.createElement(Link, { to: getOrderLinkTo(orderId) },
                            "#",
                            orderFriendlyId),
                        ")"))));
            }
            if (gift.type === 'GIFT_AVAILABLE') {
                return (React.createElement(React.Fragment, null,
                    "This gift has a selected winner:",
                    React.createElement("br", null),
                    gift.winner,
                    " (not redeemed yet)",
                    React.createElement("br", null),
                    redoButton));
            }
            if (gift.type === 'GIFT_CANCELLED') {
                return (React.createElement(React.Fragment, null,
                    "This gift has a canceled winner:",
                    React.createElement("br", null),
                    gift.winner));
            }
            if (gift.type === 'GIFT_CHANGED_TO_PROMOTION') {
                return (React.createElement(React.Fragment, null,
                    "This gift has a selected winner:",
                    React.createElement("br", null),
                    gift.winner,
                    " (redeemed as",
                    ' ',
                    React.createElement(Link, { to: getPromotionLinkTo(gift.promotionId) }, "promo code"),
                    ")",
                    React.createElement("br", null),
                    redoButton));
            }
            return null;
        }
        return (React.createElement(React.Fragment, null,
            giftsWithAvailableWinners.length > 0 && (React.createElement(React.Fragment, null,
                "This gift has ",
                giftsWithAvailableWinners.length,
                " selected winners:",
                React.createElement("ul", null, giveaway.gifts.map((gift) => {
                    if (gift.type === 'GIFT_REDEEMED') {
                        const { supporter, orderId, orderFriendlyId } = gift;
                        return (React.createElement("li", null,
                            supporter.username || supporter.email,
                            !isMembershipsGift(orderFriendlyId) && (React.createElement(React.Fragment, null,
                                ' ',
                                "(",
                                React.createElement(Link, { to: getOrderLinkTo(orderId) },
                                    "#",
                                    orderFriendlyId),
                                ")"))));
                    }
                    if (gift.type === 'GIFT_AVAILABLE') {
                        return React.createElement("li", null,
                            gift.winner,
                            " (not redeemed yet)");
                    }
                    if (gift.type === 'GIFT_CHANGED_TO_PROMOTION') {
                        return (React.createElement("li", null,
                            gift.winner,
                            " (redeemed as",
                            ' ',
                            React.createElement(Link, { to: getPromotionLinkTo(gift.promotionId) }, "promo code"),
                            ")"));
                    }
                    return null;
                })),
                React.createElement("br", null),
                redoButton)),
            giftsWithCancelledWinners.length > 0 && (React.createElement(React.Fragment, null,
                giftsWithAvailableWinners.length > 0 && React.createElement("br", null),
                "Canceled gift winners:",
                React.createElement("ul", null, giftsWithCancelledWinners.map((gift) => (React.createElement("li", null, gift.winner)))))),
            giftsWithoutWinners.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement("br", null),
                giftsWithoutWinners.length,
                ' ',
                pluralize(giftsWithoutWinners.length, `item has no winner yet.`, `items have no winners yet.`),
                React.createElement("br", null),
                redoButton))));
    };
    return [
        {
            children: getChildren(),
            appearance: 'warning',
        },
    ];
};
